import {CreateAccessor} from "mobx-graphlink";
import {Vec2Simple} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {MapObject} from "../../@MapObject.js";
import {ObjPath} from "../ObjPath.js";
import {GetMapObjects} from "../../../mapObjects.js";
import {ApplyPathTransformsToPoints, MeshesFromPointSets} from "./PathTransforms_Pure.js";
import type {ResolvedDerivedPointSets, InterpolateSeedContext} from "./PathTransforms_Pure.js";
import {Assert} from "js-vextensions";
import {PathTerrainFeature} from "../../../../../Terrain/Heights/IsolineTypes.js";
import {ResolveObjPosInWorld} from "../../$mapObject.js";

export const ResolveOwnPathID = CreateAccessor((path: ObjPath, obj: MapObject)=>{
	const pathIndex = obj.paths?.indexOf(path);
	Assert(pathIndex >= 0, "Could not find path in object's path-array.");
	return path.path_id ?? `fallback_${obj.id}_${pathIndex}`;
});

/** Unlike ResolveOriginPathForPath, this will never return null; if an inherit chain is broken, it falls back to path's own id. */
export const ResolveSeedingIDForPath = CreateAccessor((path: ObjPath, currentObj: MapObject): string=>{
	// if path is set to use source path-id, try to do so (if not, gracefully fall back to path's own id)
	if (path.inheritPoints?.useSourcePathIDForSeeding) {
		const originPathInfo = ResolveOriginPathForPath(path, currentObj);
		if (originPathInfo) {
			return ResolveOwnPathID(originPathInfo.path, originPathInfo.obj);
		}
	}
	return ResolveOwnPathID(path, currentObj);
});

/** Find the source path for a `derivePoints.source_pathID`.
 *  Searches `currentObj.paths` first; if not found, searches every map object in the same map
 *  and returns the first path with a matching `path_id`. Returns the source path, or null. */
export const FindPathByID = CreateAccessor((source_pathID: string|n, currentObj: MapObject): {path: ObjPath, obj: MapObject}|n=>{
	if (source_pathID == null) return null;
	const localMatch = currentObj.paths?.find(p=>p.path_id === source_pathID);
	if (localMatch != null) return {path: localMatch, obj: currentObj};
	// Search all map objects in the same map.
	const allObjects = GetMapObjects(currentObj.map);
	for (const obj of allObjects) {
		if (obj.paths == null) continue;
		const match = obj.paths.find(p=>p.path_id === source_pathID);
		if (match != null) return {path: match, obj};
	}
	return null;
});

export const ResolveOriginPathForPath = CreateAccessor((path: ObjPath, currentObj: MapObject): {path: ObjPath, obj: MapObject}|n=>{
	let originPathInfo: {path: ObjPath, obj: MapObject}|n = {path, obj: currentObj};
	if (path.inheritPoints) {
		const inheritPath = FindPathByID(path.inheritPoints.source_pathID, currentObj);
		if (inheritPath?.path == path) return null; // broken/invalid chain (self-reference)
		const inheritPath_resolved = inheritPath ? ResolveOriginPathForPath(inheritPath.path, inheritPath.obj) : null;
		// (note: if path-chain is invalid, we WANT origin-path-info to become null here)
		originPathInfo = inheritPath_resolved;
	}
	return originPathInfo;
});

/** Resolve the fBm-noise seed context for a path — the (pathId, worldSeed, useWorldSeed)
 *  triple that `ApplyPathTransformsToPoints` threads into the fBm interpolate step. Mirrors
 *  the pre-refactor `PathMeshUI.tsx` / `TessellateFbmPath` seed convention so the rendered
 *  curve and the solved plateau outline agree.
 *  - `pathId`: the path's resolved seeding-id (honours `inheritPoints.useSourcePathIDForSeeding`).
 *  - `useWorldSeed`: true iff the path has an active terrain-feature (the former "is isoline"
 *    gate — only then does the world-seed fold into the RNG).
 *  - `worldSeed`: the first enabled `terrainDefinition.seed` found across the map's objects. */
export const ResolveInterpolateSeedContext = CreateAccessor((path: ObjPath, currentObj: MapObject): InterpolateSeedContext=>{
	const pathId = ResolveSeedingIDForPath(path, currentObj);
	const useWorldSeed = PathTerrainFeature.IsActive(path.terrainFeature);
	let worldSeed = 0;
	if (useWorldSeed) {
		const allMapObjects = GetMapObjects(currentObj.map) ?? [];
		for (const mo of allMapObjects) {
			for (const mod of mo.modifiers ?? []) {
				if (mod.enabled && mod.terrainDefinition) { worldSeed = mod.terrainDefinition.seed; break; }
			}
			if (worldSeed) break;
		}
	}
	const objWorldPos3 = ResolveObjPosInWorld(currentObj);
	const objWorldPos = objWorldPos3 ? {x: objWorldPos3.x, y: objWorldPos3.y} : null;
	return {pathId, worldSeed, useWorldSeed, objWorldPos};
});

/** Resolve the points + closed flag that `path` INHERITS from its source chain, BEFORE
 *  `path`'s own transforms are applied. The closed flag is authoritative from the origin
 *  path (the chain's bottom, which carries literal `points`) and threaded unchanged through
 *  every layer. At each link S→P, if `P.inheritPoints.applySourceTransformCount` is set and
 *  > 0, the first N of S's OWN `S.transforms` are applied to S's inherited base (using S's
 *  own `ResolveInterpolateSeedContext`) before passing the result up to P. null/0 = inherit
 *  the source's base unchanged (the pre-feature default). MobX-cached. */
export const ResolveInheritedBase = CreateAccessor((path: ObjPath, currentObj: MapObject): {points: Vec2Simple[], closed: boolean}|n=>{
	// Base case: non-inheriting path — its inherited base is its own literal points + closed.
	if (path.inheritPoints == null) {
		if (path.points == null) return null;
		return {points: path.points, closed: !!path.curve_closed};
	}
	const source = FindPathByID(path.inheritPoints.source_pathID, currentObj);
	if (source == null) return null; // broken/invalid chain
	if (source.path == path) return null; // broken/invalid chain (self-reference)
	const sourceBase = ResolveInheritedBase(source.path, source.obj);
	if (sourceBase == null) return null;
	// Apply the first N of the source's OWN transforms to the source's inherited base.
	const count = path.inheritPoints.applySourceTransformCount;
	let currentPoints = sourceBase.points;
	if (count != null && count > 0 && Number.isFinite(count)) {
		const sourceTransforms = source.path.transforms ?? [];
		const toApply = sourceTransforms.slice(0, Math.min(count, sourceTransforms.length));
		if (toApply.length > 0) {
			const sourceSeedCtx = ResolveInterpolateSeedContext(source.path, source.obj);
			const result = ApplyPathTransformsToPoints(sourceBase.points, sourceBase.closed, toApply, sourceSeedCtx);
			currentPoints = result.currentPoints;
		}
	}
	return {points: currentPoints, closed: sourceBase.closed};
});

/** Resolve base points + per-transform per-copy arrays, for consumers needing the raw
 *  per-copy structure (rather than the mesh-projected point-sets). MobX-cached.
 *
 *  The inheritance chain is resolved recursively via `ResolveInheritedBase`: each link S→P
 *  threads S's inherited base (with the first `P.inheritPoints.applySourceTransformCount` of
 *  S's own transforms applied, using S's seedCtx) up to P; P then applies its OWN
 *  `P.transforms` on top using P's own seedCtx. The closed flag is authoritative from the
 *  origin path and threaded unchanged. For non-inheriting paths this reduces to applying
 *  `path.transforms` to `path.points` (identical to the pre-feature behaviour). */
export const ResolvePathPointSets = CreateAccessor((path: ObjPath, currentObj: MapObject): ResolvedDerivedPointSets|n=>{
	const inherited = ResolveInheritedBase(path, currentObj);
	if (inherited == null) return null;
	const seedCtx = ResolveInterpolateSeedContext(path, currentObj);
	return ApplyPathTransformsToPoints(inherited.points, inherited.closed, path.transforms ?? [], seedCtx);
});

/** Resolve a path's point-sets for the mesh renderer, paired with the AUTHORITATIVE
 *  `closed` flag for those meshes. Non-derived (or unresolvable) paths return a single
 *  entry (the literal `points`) with `closed === !!path.curve_closed`. Derived paths return
 *  one entry per mesh as selected by the last transform's `meshGen` (eachCopySeparate /
 *  origToEachCopy / copyToCopy). The returned `closed` is the resolved flag threaded up by
 *  `ResolveInheritedBase` (the origin path's `curve_closed`, possibly overridden by
 *  inheritance) — NOT necessarily `path.curve_closed` of the (possibly inheriting) `path`.
 *  Consumers that need the closed state of the tessellated output MUST read the returned
 *  `closed` here, not the raw `path.curve_closed`. MobX-cached. */
export const ResolvePathMeshes = CreateAccessor((path: ObjPath, currentObj: MapObject): {meshes: Vec2Simple[][], closed: boolean}|n=>{
	const resolved = ResolvePathPointSets(path, currentObj);
	if (resolved == null) return null;
	return {meshes: MeshesFromPointSets(resolved), closed: resolved.closed};
});